import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

const PAYMENT_METHOD_MAP = {
  Cash: 'cash',
  UPI: 'upi',
  Cheque: 'cheque',
  'Bank Transfer': 'bank_transfer',
};

// Local-timezone "today" string (YYYY-MM-DD) for the date input's max
// attribute. toISOString() is UTC and can lag a day behind IST near midnight,
// which would wrongly block today's date for users.
const todayMax = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Defined OUTSIDE the component so its identity is stable across renders.
// (Defining this inside ContributionForm would recreate the function on
// every keystroke, causing React to unmount/remount the input each time —
// which is why only one character could be typed before losing focus.)
const Field = ({ label, error, children, required = true, className = 'mb-4' }) => (
  <div className={className}>
    <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </p>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const ContributionForm = ({
  orgId,
  getToken,
  onBack,
  onHome,
  onEditProfile,
}) => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation('contributionForm');
  const { t: tc } = useTranslation('common');

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  const [form, setForm] = useState({
    name: '',
    govId: '',
    email: '',
    phone: '',
    amount: '',
    date: '',
    paymentMode: '',
    bankAccount: '',
    bankAc: '',
    project: '',
    group: '',
    category: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const formRef = useRef(null); // for Enter-to-next-field navigation

  // ---- Category dropdown (global list, optional field) ----
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/api/contributions/categories/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return; // category dropdown is optional — fail quietly
        const data = await res.json();
        setCategories(data.results || []);
      } catch (e) {
        // Optional field — don't block the form if this fails.
      }
    })();
  }, [getToken]);

  // ---- Bank accounts (same endpoint/data-fetching pattern as AddExpensePage) ----
  // Used to credit the selected account when the contribution is not cash.
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/api/expenses/bank-accounts/?org_id=${orgId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setBankAccounts(data.results || []);
      } catch (e) {
        // Dropdown is only meaningful for non-cash payments — fail quietly.
      }
    })();
  }, [orgId, getToken]);

  // Bank Account is only relevant when the payment is not cash.
  const needsBankAccount = form.paymentMode && form.paymentMode !== 'Cash';

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim() === '')
      e.name = t('validation.nameRequired');
    if (!form.govId || form.govId.trim() === '')
      e.govId = t('validation.govIdRequired');
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email))
      e.email = t('validation.emailRequired');
    if (!form.phone || form.phone.length !== 10)
      e.phone = t('validation.phoneRequired');
    if (!form.amount || Number(form.amount) <= 0)
      e.amount = t('validation.amountRequired');
    if (!form.date) {
      e.date = t('validation.dateRequired');
    } else {
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999); // allow the full current day
      if (new Date(form.date) > todayEnd) {
        e.date = t('validation.dateFuture');
      }
    }
    if (!form.paymentMode)
      e.paymentMode = t('validation.paymentModeRequired');
    if (needsBankAccount && !form.bankAccount)
      e.bankAccount = t('validation.bankAccountRequired');
    if (form.paymentMode === 'Bank Transfer' && (!form.bankAc || form.bankAc.trim() === ''))
      e.bankAc = t('validation.bankAcRequired');
    if (!form.project || form.project.trim() === '')
      e.project = t('validation.projectRequired');
    if (!form.group || form.group.trim() === '')
      e.group = t('validation.groupRequired');
    return e;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setApiError('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowConfirm(false);
      return;
    }
    setErrors({});
    setLoading(true);

    try {
      const token = await getToken();

      const payload = {
        org_id: orgId,
        name: form.name,
        gov_id: form.govId,
        email: form.email,
        phone: form.phone,
        project_name: form.project,
        group: form.group,
        amount: Number(form.amount),
        date: form.date,
        payment_method: PAYMENT_METHOD_MAP[form.paymentMode] || form.paymentMode,
        bank_reference: form.bankAc || '',
        bank_account_id: needsBankAccount && form.bankAccount ? Number(form.bankAccount) : null,
        category_id: form.category || null,
      };

      const res = await fetch(`${API_BASE}/api/contributions/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        const detail = typeof data.detail === 'string'
          ? data.detail
          : typeof data.detail === 'object'
          ? JSON.stringify(data.detail)
          : data.error || 'Something went wrong. Please try again.';
        setApiError(detail);
        setShowConfirm(false);
        return;
      }

      setSuccess(true);
      setShowConfirm(false);
    } catch (err) {
      setApiError(t('networkError'));
      setShowConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSuccess(false);
    setApiError('');
    setErrors({});
    setForm({
      name: '',
      govId: '',
      email: '',
      phone: '',
      amount: '',
      date: '',
      paymentMode: '',
      bankAccount: '',
      bankAc: '',
      project: '',
      group: '',
      category: '',
    });
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center max-w-sm w-full mx-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="text-base font-medium text-gray-900 mb-1">{t('successTitle')}</p>
          <p className="text-sm text-gray-500 mb-6">{t('successMessage')}</p>
          <button
            onClick={handleReset}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition mb-2"
          >
            {t('addAnother')}
          </button>
          <button
            onClick={onBack}
            className="w-full border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-4 py-2.5 text-sm font-medium transition"
          >
            {t('backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  const inputCls = "w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0";

  // Pressing Enter in any field moves focus to the next field instead of
  // doing nothing / submitting the form early.
  const handleFieldKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (e.target.tagName === 'BUTTON') return; // let buttons behave normally
    e.preventDefault();

    const focusable = Array.from(
      formRef.current.querySelectorAll('input, select, textarea')
    ).filter((el) => !el.disabled && el.tabIndex !== -1 && el.offsetParent !== null);

    const currentIndex = focusable.indexOf(e.target);
    if (currentIndex > -1 && currentIndex < focusable.length - 1) {
      focusable[currentIndex + 1].focus();
    } else if (currentIndex === focusable.length - 1) {
      // Last field — Enter opens the confirmation step, same as clicking Submit
      setShowConfirm(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {/* Home icon */}
        <button onClick={onHome} className="text-blue-600 hover:text-blue-700 flex-shrink-0" aria-label="Home" title="Home">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        {/* Back arrow */}
        <button onClick={onBack} className="text-blue-600 hover:text-blue-700" aria-label="Back" title="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('topBar')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getToken().then(token => {
                    fetch(`${API_BASE}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getToken().then(token => {
                    fetch(`${API_BASE}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* Profile avatar button + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div ref={formRef} onKeyDown={handleFieldKeyDown} className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* API-level error banner */}
        {apiError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{apiError}</p>
          </div>
        )}

        <Field label={t('name')} error={errors.name}>
          <input
            type="text"
            placeholder={t('namePlaceholder')}
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className={inputCls}
          />
        </Field>

        <Field label={t('amount')} error={errors.amount}>
          <input
            type="number"
            min="1"
            placeholder={t('amountPlaceholder')}
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            className={inputCls}
          />
        </Field>

        <Field label={t('govId')} error={errors.govId}>
          <input
            type="text"
            placeholder={t('govIdPlaceholder')}
            value={form.govId}
            onChange={e => setForm({ ...form, govId: e.target.value })}
            className={inputCls}
          />
        </Field>

        <Field label={t('date')} error={errors.date}>
          <input
            type="date"
            max={todayMax()}
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            className={inputCls}
          />
        </Field>

        <Field label={t('phone')} error={errors.phone}>
          <input
            type="tel"
            maxLength={10}
            placeholder={t('phonePlaceholder')}
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
            className={inputCls}
          />
        </Field>

        <Field label={t('paymentMode')} error={errors.paymentMode}>
          <select
            value={form.paymentMode}
            onChange={e => setForm({
              ...form,
              paymentMode: e.target.value,
              // Clear the selected bank account when switching to cash.
              bankAccount: e.target.value === 'Cash' ? '' : form.bankAccount,
            })}
            className={inputCls}
          >
            <option value="">{t('selectPaymentMode')}</option>
            <option value="Cash">{t('cash')}</option>
            <option value="Bank Transfer">{t('bankTransfer')}</option>
            <option value="UPI">{t('upi')}</option>
            <option value="Cheque">{t('cheque')}</option>
          </select>
        </Field>

        {needsBankAccount && (
          <Field label={t('bankAccount')} error={errors.bankAccount}>
            <select
              value={form.bankAccount}
              onChange={e => setForm({ ...form, bankAccount: e.target.value })}
              className={inputCls}
            >
              <option value="">{t('selectBankAccount')}</option>
              {bankAccounts.length > 0 ? (
                bankAccounts.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} (₹{b.balance})
                  </option>
                ))
              ) : (
                <option disabled>{t('noBankAccounts')}</option>
              )}
            </select>
          </Field>
        )}

        {form.paymentMode === 'Bank Transfer' && (
          <Field label={t('bankReference')} error={errors.bankAc}>
            <input
              type="text"
              placeholder={t('bankReferencePlaceholder')}
              value={form.bankAc}
              onChange={e => setForm({ ...form, bankAc: e.target.value })}
              className={inputCls}
            />
          </Field>
        )}

        <Field label={t('email')} error={errors.email}>
          <input
            type="email"
            placeholder={t('emailPlaceholder')}
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            className={inputCls}
          />
        </Field>

        <Field label={t('project')} error={errors.project}>
          <input
            type="text"
            placeholder={t('projectPlaceholder')}
            value={form.project}
            onChange={e => setForm({ ...form, project: e.target.value })}
            className={inputCls}
          />
        </Field>

        <Field label={t('group')} error={errors.group}>
          <input
            type="text"
            placeholder={t('groupPlaceholder')}
            value={form.group}
            onChange={e => setForm({ ...form, group: e.target.value })}
            className={inputCls}
          />
        </Field>

        <Field label={t('category')} required={false} className="mb-8">
          <select
            value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })}
            className={inputCls}
          >
            <option value="">{t('noCategory')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>

        <button
          onClick={() => setShowConfirm(true)}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 text-sm font-medium transition mt-4"
        >
          {loading ? tc('submitting') : t('submitButton')}
        </button>

      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 w-full max-w-xs text-center shadow-xl">
            <p className="text-sm font-medium text-gray-900 mb-6">{t('confirmTitle')}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {loading ? tc('submitting') : t('confirmButton')}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                className="w-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg py-2.5 text-sm font-medium transition"
              >
                {t('rejectButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContributionForm;